// components/common/SearchableSelect.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

export interface SearchableSelectOption {
  id: string;
  name: string;
  difficulty?: number;
  [key: string]: any;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  style?: React.CSSProperties;
  renderOption?: (option: SearchableSelectOption) => React.ReactNode;
  filterFunction?: (option: SearchableSelectOption, searchTerm: string) => boolean;
  maxHeight?: string;
  error?: string;
  showDifficulty?: boolean;
  required?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  className = "",
  style = {},
  renderOption,
  filterFunction,
  maxHeight = "12rem",
  error,
  showDifficulty = false,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SearchableSelectOption[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultFilterFunction = useCallback((option: SearchableSelectOption, searchTerm: string) => {
    return option.name.toLowerCase().includes(searchTerm.toLowerCase());
  }, []);

  const filterFunc = filterFunction || defaultFilterFunction;

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(options.filter(option => filterFunc(option, searchTerm)));
    }
  }, [searchTerm, options, filterFunc]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.id === value);

  const handleSelect = (option: SearchableSelectOption) => {
    onChange(option.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const defaultRenderOption = (option: SearchableSelectOption) => (
    <div className="flex items-center justify-between w-full">
      <span className="flex-1 mr-2 leading-normal break-words">
        {option.name}
      </span>
      {showDifficulty && option.difficulty !== undefined && (
        <span className="text-sm text-gray-500 flex-shrink-0 mt-0.5">
          {option.difficulty.toFixed(1)}
        </span>
      )}
    </div>
  );

  const renderOptionContent = renderOption || defaultRenderOption;

  const baseStyle: React.CSSProperties = {
    position: 'relative',
    display: 'block',
    width: '100%',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#374151',
    ...style
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem',
    border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '0.25rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
    color: disabled ? '#9ca3af' : '#111827',
    minHeight: '2.5rem',
    boxShadow: error ? '0 0 0 1px #ef4444' : 'none'
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 50,
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    maxHeight: '16rem',
    overflow: 'hidden'
  };

  return (
    <div className={className} style={baseStyle}>
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div
          onClick={handleToggle}
          style={selectStyle}
          role="button"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span style={{
            flex: 1,
            marginRight: '0.5rem',
            lineHeight: '1.4',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {selectedOption ? selectedOption.name : placeholder}
          </span>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        
        {isOpen && !disabled && (
          <div style={dropdownStyle}>
            <div style={{
              padding: '0.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{ position: 'relative' }}>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  style={{
                    width: '100%',
                    paddingLeft: '0.75rem',
                    paddingRight: '2rem',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                    outline: 'none',
                    backgroundColor: '#ffffff',
                    color: '#111827'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    style={{
                      position: 'absolute',
                      right: '0.5rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            
            <div style={{
              maxHeight: maxHeight,
              overflowY: 'auto'
            }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      backgroundColor: option.id === value ? '#eff6ff' : 'transparent',
                      color: option.id === value ? '#2563eb' : '#111827'
                    }}
                    role="option"
                    aria-selected={option.id === value}
                  >
                    {renderOptionContent(option)}
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '0.5rem 0.75rem',
                  color: '#6b7280',
                  fontSize: '0.875rem'
                }}>
                  No options found matching "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <div style={{
          marginTop: '0.25rem',
          fontSize: '0.875rem',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;